/**
 * TieredSubStatusSelect — nested two-level dropdown for sub-status pickers.
 *
 * Pipeline v2.0 spec (May 2026, Danny): Contacted has 24 sub-statuses
 * grouped into 5 tiers. Rather than showing a flat 24-item dropdown
 * (which forces the rep to scan everything), this component surfaces
 * the TIERS first with colored dots, then expands the relevant tier
 * inline. When the rep already knows the value, they can also use the
 * inline search at the top to filter across all tiers.
 *
 * Behavior:
 *   - If the stage has any sub-status with a `tier` field, render the
 *     two-level UI.
 *   - Otherwise (Demo, Post-Demo, Proposal, etc.), render a flat list
 *     — same UX as a native <select> but consistent visuals.
 *
 * Props:
 *   options : Array<{ value, label, tier? }>
 *   value   : string  (currently selected sub-status)
 *   onChange: (newValue: string) => void
 *   onOutcome?: (newValue: string, outcome: "Voicemail" | "Conversation" | "Meeting Scheduled") => void
 *               // Iter 178.9 (Lisa #f112166d Feb 2026) — when set,
 *               // selecting a Contacted-tier sub-status (gatekeeper /
 *               // admin / clinical / executive) opens a second pane
 *               // asking what happened on that contact attempt. The
 *               // outcome is sent to the parent so it can be logged
 *               // separately from the sub-status update (Lisa wants
 *               // this data to drive follow-up campaigns).
 *   testid  : string  (root data-testid)
 */
import { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDown, ChevronRight, Check, Search, X, Phone, MessageSquare, Calendar } from "lucide-react";

// Tier definitions — keep in sync with /app/pipeline_stages_revised (2).md
export const TIER_META = {
  gatekeeper: { label: "Gatekeeper",      dot: "#9CA3AF", order: 1 },  // gray
  admin:      { label: "Administrative",  dot: "#0A84FF", order: 2 },  // Apple blue
  clinical:   { label: "Physician / Clinical", dot: "#10B981", order: 3 },  // green
  executive:  { label: "Executive",       dot: "#8B5CF6", order: 4 },  // purple
  no_contact: { label: "No live contact", dot: "#6B7280", order: 5 },  // dark gray
  // Demo-stage tiers (Iter 246) — "Demo Completed" is a group whose
  // items are the demo outcome sub-sub-statuses.
  demo_status:    { label: "Demo status",    dot: "#0A84FF", order: 1 },  // Apple blue
  demo_completed: { label: "Demo Completed", dot: "#10B981", order: 2 },  // green
};

// Iter 178.9 (Lisa #f112166d Feb 2026) — second-step outcome choices
// that appear after a Contacted-tier sub-status is picked. Order
// = display order (most-common first). Each carries the lucide
// icon to use in the chip + a tint color.
const OUTCOME_CHOICES = [
  { value: "Voicemail",         label: "Voicemail",         icon: Phone,        tint: "#0A84FF" },
  { value: "Conversation",      label: "Conversation",      icon: MessageSquare,tint: "#10B981" },
  { value: "Meeting Scheduled", label: "Meeting Scheduled", icon: Calendar,     tint: "#8B5CF6" },
];
// Tiers that should surface the outcome second-step. "no_contact"
// tier is excluded — picks like "No answer", "Wrong number" already
// describe the outcome themselves.
const OUTCOME_ELIGIBLE_TIERS = new Set(["gatekeeper", "admin", "clinical", "executive"]);

export default function TieredSubStatusSelect({ options, value, onChange, onOutcome, testid = "tiered-substatus-select" }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedTier, setExpandedTier] = useState(null);
  // Iter 178.9 (Lisa #f112166d) — when the rep picks a Contacted-
  // tier sub-status and `onOutcome` was wired by the parent, we
  // stash the pick here and show the outcome chooser instead of
  // closing the popover. Cleared on outcome click or popover close.
  const [pendingPick, setPendingPick] = useState(null); // { value, tierKey }
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  // Compute tiered structure (or null when this stage isn't tiered).
  const tiered = useMemo(() => {
    const hasTiers = (options || []).some(o => o.tier);
    if (!hasTiers) return null;
    const groups = {};
    for (const opt of options || []) {
      const t = opt.tier || "no_contact";
      if (!groups[t]) groups[t] = [];
      groups[t].push(opt);
    }
    return Object.entries(groups)
      .map(([key, items]) => ({ key, meta: TIER_META[key] || TIER_META.no_contact, items }))
      .sort((a, b) => (a.meta.order || 99) - (b.meta.order || 99));
  }, [options]);

  // Pre-select the tier of the current value when opening.
  useEffect(() => {
    if (!open || !tiered) return;
    const cur = (options || []).find(o => o.value === value);
    if (cur?.tier) setExpandedTier(cur.tier);
  }, [open, tiered, options, value]);

  // Close on outside click + escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    // Focus search after the popover paints.
    setTimeout(() => searchRef.current?.focus(), 60);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const filteredFlat = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.trim().toLowerCase();
    return (options || []).filter(o => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const currentOpt = (options || []).find(o => o.value === value);
  const currentTier = currentOpt?.tier ? TIER_META[currentOpt.tier] : null;

  function pick(v) {
    // Iter 178.9 — if the picked option has a Contacted tier AND the
    // parent gave us an `onOutcome` callback, enter the outcome
    // chooser instead of closing immediately. The actual sub-status
    // update still gets persisted via onChange — only the popover
    // close is deferred until the rep picks (or skips) an outcome.
    const opt = (options || []).find(o => o.value === v);
    if (onOutcome && opt && opt.tier && OUTCOME_ELIGIBLE_TIERS.has(opt.tier)) {
      onChange(v);
      setPendingPick({ value: v, tierKey: opt.tier, label: opt.label });
      // Keep the popover open and clear the search so the outcome
      // pane gets the full popover width.
      setSearch("");
      return;
    }
    onChange(v);
    setOpen(false);
    setSearch("");
  }
  function chooseOutcome(outcome) {
    if (!pendingPick) return;
    try { onOutcome?.(pendingPick.value, outcome); } catch { /* parent threw — non-fatal */ }
    setPendingPick(null);
    setOpen(false);
  }
  function skipOutcome() {
    setPendingPick(null);
    setOpen(false);
  }
  // Close popover ⇒ also clear any pending pick so reopening starts fresh.
  useEffect(() => { if (!open) setPendingPick(null); }, [open]);

  return (
    <div ref={rootRef} style={{ position: "relative" }} data-testid={testid}>
      <button
        type="button"
        data-testid={`${testid}-trigger`}
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 8,
          padding: "6px 8px", height: 32,
          background: "#fff", border: "0.5px solid var(--border, #d6d6d6)",
          borderRadius: 7, fontSize: 13, fontFamily: "inherit", color: "#1d1d1f",
          cursor: "pointer", textAlign: "left",
        }}
        aria-haspopup="listbox" aria-expanded={open}
      >
        {currentTier && (
          <span aria-hidden style={{ width: 7, height: 7, borderRadius: "50%", background: currentTier.dot, flexShrink: 0 }} />
        )}
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: value ? "#1d1d1f" : "var(--text-tertiary, #888)" }}>
          {value ? (currentOpt?.label || `${value} (legacy)`) : "— Pick a sub-status —"}
        </span>
        <ChevronDown style={{ width: 12, height: 12, color: "var(--text-tertiary, #888)", flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
      </button>

      {open && (
        <div
          data-testid={`${testid}-popover`}
          style={{
            position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4,
            zIndex: 60,
            background: "#fff",
            border: "0.5px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 10,
            boxShadow: "0 8px 28px -8px rgba(10, 22, 40, 0.18), 0 2px 4px rgba(10, 22, 40, 0.05)",
            maxHeight: 360,
            display: "flex", flexDirection: "column",
            overflow: "hidden",
            minWidth: 280,
          }}
        >
          {/* Search */}
          <div style={{ position: "relative", padding: "8px 10px 4px", borderBottom: "0.5px solid var(--border, #ececec)" }}>
            <Search style={{ position: "absolute", left: 18, top: 14, width: 12, height: 12, color: "var(--text-tertiary, #888)", pointerEvents: "none" }} />
            <input
              ref={searchRef}
              data-testid={`${testid}-search`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search sub-statuses…"
              style={{
                width: "100%", padding: "5px 22px 5px 24px",
                fontSize: 12, fontFamily: "inherit",
                border: "0.5px solid var(--border, #e2e2e2)", borderRadius: 6,
                outline: "none", color: "#1d1d1f", background: "#fafafa",
              }}
            />
            {search && (
              <button onClick={() => setSearch("")} data-testid={`${testid}-search-clear`}
                style={{ position: "absolute", right: 16, top: 12, background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary, #888)", padding: 2 }}>
                <X style={{ width: 12, height: 12 }} />
              </button>
            )}
          </div>

          {/* Body — outcome chooser (Iter 178.9) when a Contacted-tier
              pick is pending; otherwise: filtered flat list, tiered
              groups, or flat (untiered stage). */}
          <div style={{ overflowY: "auto", flex: 1, padding: "4px 0" }}>
            {pendingPick ? (
              <div data-testid={`${testid}-outcome-pane`} style={{ padding: "8px 12px 12px 12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <span aria-hidden style={{ width: 8, height: 8, borderRadius: "50%", background: TIER_META[pendingPick.tierKey]?.dot || "#888", flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: "var(--text-secondary, #555)", flex: 1 }}>
                    Logged: <strong style={{ color: "#1d1d1f" }}>{pendingPick.label}</strong>
                  </span>
                </div>
                <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-secondary, #555)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
                  What happened?
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {OUTCOME_CHOICES.map(oc => {
                    const Icon = oc.icon;
                    return (
                      <button
                        key={oc.value}
                        type="button"
                        data-testid={`${testid}-outcome-${oc.value.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => chooseOutcome(oc.value)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 9,
                          padding: "9px 11px",
                          background: "#fafafa",
                          border: "0.5px solid var(--border, #e2e2e2)",
                          borderRadius: 8,
                          cursor: "pointer",
                          fontFamily: "inherit",
                          color: "#1d1d1f",
                          fontSize: 12,
                          fontWeight: 500,
                          textAlign: "left",
                          transition: "background 0.12s ease, border-color 0.12s ease",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "#f1f5f9"; e.currentTarget.style.borderColor = oc.tint; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "#fafafa"; e.currentTarget.style.borderColor = "var(--border, #e2e2e2)"; }}
                      >
                        <Icon style={{ width: 13, height: 13, color: oc.tint, flexShrink: 0 }} />
                        <span style={{ flex: 1 }}>{oc.label}</span>
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  data-testid={`${testid}-outcome-skip`}
                  onClick={skipOutcome}
                  style={{
                    marginTop: 10, width: "100%",
                    padding: "5px 8px", background: "transparent",
                    border: "none", color: "var(--text-tertiary, #888)",
                    fontSize: 10, cursor: "pointer", fontFamily: "inherit",
                  }}
                >
                  Skip — just update the sub-status
                </button>
              </div>
            ) : filteredFlat ? (
              filteredFlat.length === 0 ? (
                <div style={{ padding: 16, fontSize: 12, color: "var(--text-tertiary, #888)", textAlign: "center", fontStyle: "italic" }}>No matches</div>
              ) : (
                filteredFlat.map(opt => {
                  const meta = opt.tier ? TIER_META[opt.tier] : null;
                  return (
                    <SubStatusRow
                      key={opt.value}
                      label={opt.label}
                      dot={meta?.dot}
                      selected={opt.value === value}
                      onClick={() => pick(opt.value)}
                      testid={`${testid}-option-${slugify(opt.value)}`}
                    />
                  );
                })
              )
            ) : tiered ? (
              tiered.map(group => {
                const exp = expandedTier === group.key;
                return (
                  <div key={group.key} data-testid={`${testid}-tier-${group.key}`}>
                    <button
                      type="button"
                      onClick={() => setExpandedTier(exp ? null : group.key)}
                      data-testid={`${testid}-tier-${group.key}-header`}
                      style={{
                        width: "100%", display: "flex", alignItems: "center", gap: 8,
                        padding: "7px 12px",
                        background: exp ? "#f5f7fa" : "transparent",
                        border: "none", cursor: "pointer", fontFamily: "inherit",
                        textAlign: "left", color: "#1d1d1f",
                      }}
                    >
                      <span aria-hidden style={{ width: 8, height: 8, borderRadius: "50%", background: group.meta.dot, flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: 11, fontWeight: 600, letterSpacing: "0.02em", textTransform: "uppercase", color: "var(--text-secondary, #555)" }}>
                        {group.meta.label}
                      </span>
                      <span style={{ fontSize: 10, color: "var(--text-tertiary, #999)", fontVariantNumeric: "tabular-nums" }}>
                        {group.items.length}
                      </span>
                      <ChevronRight style={{ width: 11, height: 11, color: "var(--text-tertiary, #999)", transform: exp ? "rotate(90deg)" : "none", transition: "transform 0.15s" }} />
                    </button>
                    {exp && (
                      <div style={{ paddingBottom: 4 }}>
                        {group.items.map(opt => (
                          <SubStatusRow
                            key={opt.value}
                            label={opt.label}
                            indent
                            selected={opt.value === value}
                            onClick={() => pick(opt.value)}
                            testid={`${testid}-option-${slugify(opt.value)}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              // Untiered stages — flat list with same row visuals.
              (options || []).map(opt => (
                <SubStatusRow
                  key={opt.value}
                  label={opt.label}
                  selected={opt.value === value}
                  onClick={() => pick(opt.value)}
                  testid={`${testid}-option-${slugify(opt.value)}`}
                />
              ))
            )}

            {/* Legacy off-canon value rendered at the bottom so reps can read but not re-pick it without intention */}
            {value && currentOpt === undefined && (
              <SubStatusRow
                key="__legacy__"
                label={`${value} (legacy)`}
                selected
                onClick={() => {}}
                testid={`${testid}-option-legacy`}
                italic
              />
            )}

            {/* Iter 179.7 (Lisa #10dd2c4e Feb 2026) — Undo / clear
                button. When a rep accidentally marks a lead as
                "Contacted — Front Desk / Receptionist" while just
                reviewing, they need a way to revert the lead back to
                no sub-status. Always rendered when a value is set;
                clicking it pushes "" through onChange, which the
                parent persists as a sub_status reset. */}
            {value && !pendingPick && (
              <div style={{ borderTop: "0.5px solid var(--border, #ececec)", padding: "6px 8px" }}>
                <button
                  type="button"
                  data-testid={`${testid}-clear`}
                  onClick={() => {
                    onChange("");
                    setOpen(false);
                    setSearch("");
                  }}
                  style={{
                    width: "100%", display: "flex", alignItems: "center", justifyContent: "center",
                    gap: 6, padding: "6px 8px", background: "transparent",
                    border: "none", color: "var(--text-secondary, #555)",
                    fontSize: 11, cursor: "pointer", fontFamily: "inherit", fontWeight: 500,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#f5f7fa"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  title="Clear the sub-status so this lead is no longer marked as contacted"
                >
                  <X style={{ width: 12, height: 12 }} /> Clear sub-status (undo)
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SubStatusRow({ label, dot, indent, selected, onClick, testid, italic }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 8,
        padding: `6px ${indent ? 32 : 12}px 6px ${indent ? 32 : 12}px`,
        background: selected ? "rgba(10, 132, 255, 0.10)" : "transparent",
        border: "none", borderLeft: selected ? "2px solid #0A84FF" : "2px solid transparent",
        cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, color: "#1d1d1f",
        textAlign: "left", fontStyle: italic ? "italic" : "normal",
      }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = "#f5f7fa"; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = "transparent"; }}
    >
      {dot && <span aria-hidden style={{ width: 6, height: 6, borderRadius: "50%", background: dot, flexShrink: 0 }} />}
      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
      {selected && <Check style={{ width: 12, height: 12, color: "#0A84FF", flexShrink: 0 }} />}
    </button>
  );
}

function slugify(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
